import '@yaireo/tagify/dist/tagify.css';
import './tag-autocomplete.css';

import { useCallback, useEffect, useMemo, useRef } from 'react';

import { GetTagsApiArg, useGetTagsQuery } from '@client/api';
import Tags from '@yaireo/tagify/dist/react.tagify';

import { useSearching } from '../../hooks';
import { Skeleton } from '../../ui';

const baseSettings: Tagify.TagifySettings = {
  dropdown: {
    enabled: 0,
  },
  autoComplete: {
    rightKey: true,
  },
  a11y: {
    focusableTags: false,
  },
};

export interface TagAutocompleteProps extends Omit<Tags.TagifyTagsReactProps, 'inputMode' | 'whitelist' | 'loading'> {
  queryParams?: Omit<GetTagsApiArg, 'search'>;
}

export const TagAutocomplete = ({
  onInput: propsOnInput,
  settings: propsSettings,
  showDropdown = true,
  queryParams,
  ...other
}: TagAutocompleteProps) => {
  const { debouncedSearch, updateSearch } = useSearching();
  const containerRef = useRef<HTMLDivElement>(null);
  
  const {
    data = { items: [] },
    isFetching,
    isLoading,
  } = useGetTagsQuery({
    search: debouncedSearch ?? undefined,
    ...queryParams,
  });

  // Remove tabindex="-1" from the tagify element to prevent focus stealing
  useEffect(() => {
    if (containerRef.current) {
      const tagsElement = containerRef.current.querySelector('tags.tagify');
      if (tagsElement) {
        tagsElement.removeAttribute('tabindex');
      }
    }
  }, [isLoading]);

  const suggestions = useMemo(() => data.items.map((item) => item.name), [data.items]);

  const onInput = useCallback(
    (e: CustomEvent<Tagify.InputEventData<Tagify.TagData>>) => {
      const detail = e.detail;
      propsOnInput?.(e);

      if (!('value' in detail)) return;

      updateSearch(detail.value);
    },
    [updateSearch, propsOnInput],
  );

  const settings = useMemo(() => ({ ...baseSettings, ...propsSettings }), [propsSettings]);

  if (isLoading) return <Skeleton variant="rectangular" width="w-full" height="h-10" />;

  return (
    <div ref={containerRef}>
      <Tags
        className="w-full"
        InputMode="input"
        settings={settings}
        whitelist={suggestions}
        loading={isFetching}
        onInput={onInput}
        showDropdown={showDropdown}
        {...other}
      />
    </div>
  );
};
